import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GoogleChartInterface, GoogleChartType } from 'ng2-google-charts';
import { MatDialog } from '@angular/material/dialog';
import { WebsiteAuditService } from '../website-audit.service';
import { SidenavService } from 'src/app/sidenav/sidenav.service';
import { DarkmodeService } from 'src/app/services/darkmode.service';
import { SeoService } from 'src/app/seo/seo.service';
import { AddCompetitorComponent } from '../modals/add-competitor/add-competitor.component';

const ACCENT = '#6161ff';
const COMP_COLOR = '#e0a544';

// Categories that map onto the new overview.scores / sections blocks.
const CAT_ORDER = ['ux', 'cr', 'seo', 'geo', 'technical', 'diagnostic'];
const CAT_LABELS: { [k: string]: string } = {
  ux: 'UX',
  cr: 'CR',
  seo: 'SEO',
  geo: 'GEO',
  technical: 'Technical',
  diagnostic: 'Diagnostics',
};
const CAT_TAB_INDEX: { [k: string]: number } = {
  ux: 1,
  cr: 2,
  seo: 3,
  geo: 4,
  technical: 5,
  diagnostic: 6,
};
const CAT_COLORS: { [k: string]: string } = {
  ux: '#6161ff',
  cr: '#c98ae0',
  seo: '#5b9df0',
  geo: '#5fcf95',
  technical: '#e0a544',
  diagnostic: '#e26a5d',
};

// Top-critical-issue pills cycle through these by position: blue, purple,
// orange, green, red — so consecutive pills always read as distinct.
const PILL_PALETTE = ['#5b9df0', '#c98ae0', '#e0a544', '#5fcf95', '#e26a5d'];

// Distinct line/bar colors for each tracked competitor in the Overview trend
// and standings, so multiple competitors read as clearly separate series.
// COMP_COLOR (amber) stays first to match the single-competitor look.
const COMP_PALETTE = [
  '#e0a544',
  '#5b9df0',
  '#c98ae0',
  '#5fcf95',
  '#e26a5d',
  '#4da18c',
  '#dd7bb0',
  '#c8e6a0',
];

// Categories whose scale is inverted (a HIGHER number is WORSE) — the color,
// status, delta direction and competitor comparison all flip for these.
// Diagnostics used to be inverted, but it now reads higher-is-better like every
// other category, so nothing is inverted currently.
const INVERTED_SCORE_KEYS: string[] = [];

@Component({
  selector: 'app-overview',
  templateUrl: './overview.component.html',
  styleUrls: ['../audit-theme.css', './overview.component.css'],
})
export class OverviewComponent implements OnInit, OnDestroy {
  constructor(
    private sidenavService: SidenavService,
    private websiteAuditService: WebsiteAuditService,
    private darkmodeService: DarkmodeService,
    private seoService: SeoService,
    private dialog: MatDialog,
  ) {}

  // Shared read-only view — hide mutating actions like "Add competitor".
  public isShare = window.location.href.includes('/share/website-audit');

  private subs: Subscription = new Subscription();
  // Overlay shown while the sub-bar swaps competitors.
  public competitorSwitching$ = this.websiteAuditService.getCompetitorSwitching();

  // colors exposed for the template
  accent = ACCENT;
  compColor = COMP_COLOR;

  // semantic colors — theme-aware (soft mint reads well on dark, deeper green on light)
  green = '#5fcf95';
  red = '#e26a5d';
  amber = '#e0a544';
  gray = '#6b6660';

  private isDark = true;

  private applyThemeColors(isDark: boolean) {
    this.isDark = isDark;
    if (isDark) {
      this.green = '#5fcf95';
      this.red = '#e26a5d';
      this.amber = '#e0a544';
      this.gray = '#6b6660';
    } else {
      this.green = '#16a34a';
      this.red = '#d1483b';
      this.amber = '#b9791a';
      this.gray = '#6b7280';
    }
  }

  // states
  public isLoading = false;
  public showLoader = false;
  public noData = false;
  public loaderPercentage = 0;
  public loaderLineIndex = 0;
  public loaderTexts: string[] = [
    'Initializing audit...',
    'Collecting site data...',
    'Reviewing structure...',
    'Checking key elements...',
    'Analyzing performance signals...',
    'Evaluating overall quality...',
    'Identifying potential issues...',
    'Cross-checking results...',
    'Compiling insights...',
    'Finalizing audit overview...',
  ];

  public selectedTask: any;
  public overviewData: any = null;
  private sectionsData: any = null;

  // competitors
  public competitors: any[] = [];
  public activeCompetitor: any = null;

  // header/sub-bar
  public periodMain = '';
  public periodCompare = '';

  // executive summary
  public summaryHeadline = '';
  public summaryText = '';
  public kpiHealth = 0;
  public kpiHealthDelta: number | null = null;
  public kpiHealthSpark: number[] = [];
  public kpiHealthStatus = '';
  public kpiHealthStatusColor = '';
  public kpiOpenIssues = 0;
  public kpiOpenDelta: number | null = null;
  public kpiOpenSpark: number[] = [];
  public kpiRank = 0;
  public kpiRankTotal = 0;
  public kpiRankNote = '';

  // category cards
  public catCards: any[] = [];

  // SEO/GEO scores come from a different API (keywords summary), not the audit
  // payload. SEO = general_obj.traditional_search (SERP), GEO = avg of the AI
  // engine scores (gpt/gemini). Cache the general objects so competitor
  // switches / theme toggles re-derive without refetching.
  private seoGeoScores: {
    [k: string]: { target: number | null; competitor: number | null; targetDelta: number | null } | null;
  } = { seo: null, geo: null };
  private targetGeneral: any = null;
  private compGeneralCache: { [domain: string]: any } = {};
  private seoGeoTaskId = '';

  // overall trend (project-standard Google LineChart)
  public overallHasData = false;
  public overallLineChart: GoogleChartInterface = {
    chartType: GoogleChartType.LineChart,
    dataTable: [],
    options: {
      legend: {
        position: 'top',
        alignment: 'center',
        textStyle: { fontSize: 12, fontName: 'Roboto, sans-serif', color: '#666' },
      },
      pointSize: 4,
      interpolateNulls: true,
      crosshair: { trigger: 'focus', orientation: 'vertical', color: '#eeeeee' },
      focusTarget: 'category',
      tooltip: { textStyle: { fontSize: 12 }, isHtml: true, trigger: 'focus' },
      colors: [ACCENT, COMP_COLOR],
      height: 300,
      backgroundColor: 'transparent',
      chartArea: { left: 44, right: 20, top: 30, bottom: 40, width: '100%', height: '72%' },
      vAxis: {
        minValue: 0,
        maxValue: 100,
        baseline: 0,
        baselineColor: '#c0c0c0',
        viewWindow: { min: 0, max: 100 },
        gridlineColor: '#ededed',
        textStyle: { fontSize: 11, fontName: 'Roboto, sans-serif', color: '#666' },
      },
      hAxis: {
        textStyle: { fontSize: 11, fontName: 'Roboto, sans-serif', color: '#666' },
        gridlines: { color: 'transparent' },
        minorGridlines: { count: 0 },
        baselineColor: 'transparent',
      },
    },
  };

  // bottom
  public topCriticalIssues: any[] = [];
  public standings: any[] = [];
  public standingsNote = '';

  // Overall trend: interactive legend so each competitor line can be toggled
  // (#14/#15). "Your site" is always shown; competitors default visible.
  public trendSeries: {
    key: string;
    label: string;
    color: string;
    isYou: boolean;
    visible: boolean;
  }[] = [];
  // Visibility per competitor key, preserved across re-renders (theme toggles,
  // competitor switches) within the same session.
  private trendVisible: { [key: string]: boolean } = {};

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.subs.add(
      this.darkmodeService.getDarkMode().subscribe((isDark: boolean) => {
        // Follows the service value everywhere, including the shared page — the
        // share shell sets the theme (from the shared-with theme) via the
        // darkmode service, so charts/colors match the sharer's theme.
        this.applyThemeColors(!!isDark);
        if (this.overviewData) {
          this.hydrate(
            this.overviewData,
            this.sectionsData,
            this.competitors,
            this.activeCompetitor,
          );
        }
      }),
    );

    this.subs.add(
      this.websiteAuditService.getSelectedAuditTask().subscribe((res: any) => {
        if (res) {
          this.noData = false;
          this.selectedTask = res;
          this.hydrate(
            res.overview,
            res.sections,
            res.availableCompetitors || [],
            res.selectedCompetitor || null,
          );
          this.updateLoaderState();
        } else {
          this.resetEmpty();
        }
      }),
    );

    this.subs.add(
      this.websiteAuditService.getAuditTasksLoading().subscribe((loading: any) => {
        this.isLoading = loading;
      }),
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  // ─── Hydration ───────────────────────────────────────────────────────────────

  private hydrate(overview: any, sections: any, competitors: any[], activeComp: any) {
    this.overviewData = overview || null;
    this.sectionsData = sections || null;
    this.competitors = Array.isArray(competitors) ? competitors : [];
    this.activeCompetitor = activeComp;

    this.summaryHeadline = overview?.summary?.headline || '';
    this.summaryText = overview?.summary?.text || '';

    this.maybeFetchSeoGeo();
    this.applySeoGeoScores();

    this.buildPeriod();
    this.buildKpis();
    this.buildCatCards();
    this.buildOverallTrend();
    this.buildTopIssues();
    this.buildStandings();
  }

  // ─── SEO / GEO scores (keywords-summary API) ─────────────────────────────────

  // Target general_obj is fetched once per task; the competitor's general_obj
  // is fetched per competitor domain (from get-competitor-summary). Both carry
  // traditional_search / gpt_performance / gemini_performance on a 0–100 scale.
  private maybeFetchSeoGeo() {
    const domain = this.getDomain(this.selectedTask?.domain || this.selectedTask?.url || '');
    if (!domain) return;

    const taskId = this.selectedTask?.taskId;
    if (taskId && this.seoGeoTaskId !== taskId) {
      this.seoGeoTaskId = taskId;
      this.targetGeneral = null;

      this.subs.add(
        this.seoService.getKeywordsData({ domain }).subscribe({
          next: (res: any) => {
            this.targetGeneral = res?.variables?.general_obj || null;
            this.applySeoGeoScores();
            this.buildCatCards();
          },
          error: () => {},
        }),
      );
    }

    const compDomain = this.getDomain(
      this.activeCompetitor?.domain || this.activeCompetitor?.url || '',
    );
    if (compDomain && !(compDomain in this.compGeneralCache)) {
      this.compGeneralCache[compDomain] = null; // mark in-flight
      this.subs.add(
        this.seoService
          .getCompetitorSummary(
            { domain: compDomain },
            this.isShare ? compDomain : undefined,
          )
          .subscribe({
            next: (res: any) => {
              this.compGeneralCache[compDomain] = res?.general_obj || {};
              this.applySeoGeoScores();
              this.buildCatCards();
            },
            error: () => {
              this.compGeneralCache[compDomain] = {};
            },
          }),
      );
    }
  }

  private applySeoGeoScores() {
    const compDomain = this.getDomain(
      this.activeCompetitor?.domain || this.activeCompetitor?.url || '',
    );
    const compGeneral = compDomain ? this.compGeneralCache[compDomain] : null;

    this.seoGeoScores['seo'] = this.buildSeoGeoEntry('seo', compGeneral);
    this.seoGeoScores['geo'] = this.buildSeoGeoEntry('geo', compGeneral);
  }

  private buildSeoGeoEntry(key: string, compGeneral: any) {
    if (!this.targetGeneral) return null;
    return {
      target: this.scoreFromGeneral(key, this.targetGeneral),
      competitor: compGeneral ? this.scoreFromGeneral(key, compGeneral) : null,
      targetDelta: null,
    };
  }

  // SEO → SERP score; GEO → mean of the available generative-engine scores.
  private scoreFromGeneral(key: string, general: any): number | null {
    if (!general) return null;
    if (key === 'seo') return this.numOrNull(general.traditional_search);

    const parts = [general.gpt_performance, general.gemini_performance]
      .map((v) => this.numOrNull(v))
      .filter((v): v is number => v !== null);
    if (!parts.length) return null;
    return parts.reduce((a, b) => a + b, 0) / parts.length;
  }

  private resetEmpty() {
    this.noData = true;
    this.overviewData = null;
    this.sectionsData = null;
    this.competitors = [];
    this.activeCompetitor = null;
    this.catCards = [];
    this.overallHasData = false;
    this.overallLineChart.dataTable = [];
    this.topCriticalIssues = [];
    this.standings = [];
    this.summaryHeadline = '';
    this.summaryText = '';
  }

  private targetSeries(): any {
    return (this.overviewData?.scoreHistory?.series || []).find(
      (s: any) => s.type === 'target',
    );
  }

  private buildPeriod() {
    const dates: string[] = this.overviewData?.scoreHistory?.dates || [];
    if (dates.length) {
      this.periodMain = this.fmtDate(dates[dates.length - 1]);
      this.periodCompare =
        dates.length > 1 ? `vs ${this.fmtDate(dates[dates.length - 2])}` : '';
    } else {
      this.periodMain = this.getAuditDateLabel();
      this.periodCompare = '';
    }
  }

  private buildKpis() {
    const scores = this.overviewData?.scores || {};
    const overall = scores.overall || {};
    const tSeries = this.targetSeries();

    this.kpiHealth =
      overall.target !== null && overall.target !== undefined
        ? Math.round(overall.target)
        : 0;
    this.kpiHealthDelta = this.roundDelta(overall.targetDelta);
    this.kpiHealthSpark = (tSeries?.scores?.overall || []).filter(
      (v: any) => v !== null && v !== undefined,
    );
    this.kpiHealthStatus = this.impactLabel(this.kpiHealth);
    this.kpiHealthStatusColor = this.impactColor(this.kpiHealth);

    // Open issues — prefer scoreHistory total series (has trend), else sum sections
    const totals: any[] = tSeries?.openIssues?.total || [];
    const cleanTotals = totals.filter((v) => v !== null && v !== undefined);
    if (cleanTotals.length) {
      this.kpiOpenSpark = cleanTotals;
      this.kpiOpenIssues = cleanTotals[cleanTotals.length - 1];
      this.kpiOpenDelta =
        cleanTotals.length > 1
          ? cleanTotals[cleanTotals.length - 1] - cleanTotals[cleanTotals.length - 2]
          : null;
    } else {
      this.kpiOpenSpark = [];
      this.kpiOpenIssues = this.sumSectionIssues();
      this.kpiOpenDelta = null;
    }

    // Market position: you vs all tracked competitors by overall score. Use each
    // competitor's latest series value; fall back to the single selected
    // competitor for one-time audits without per-competitor series.
    const compScores = this.enrichedCompetitors()
      .map((c) => this.seriesLatestOverall(c.series))
      .filter((v): v is number => v !== null);
    if (
      !compScores.length &&
      overall.competitor !== null &&
      overall.competitor !== undefined
    ) {
      compScores.push(overall.competitor);
    }

    if (compScores.length) {
      const target = overall.target || 0;
      this.kpiRankTotal = compScores.length + 1;
      this.kpiRank = 1 + compScores.filter((s) => s > target).length;
      const single = compScores.length === 1;
      if (this.kpiRank === 1) {
        const diff = Math.round(target - Math.max(...compScores));
        this.kpiRankNote = single
          ? `${diff} ahead of ${this.getActiveCompetitorName()}`
          : `${diff} ahead of nearest competitor`;
      } else {
        const diff = Math.round(Math.max(...compScores) - target);
        this.kpiRankNote = single
          ? `${diff} behind ${this.getActiveCompetitorName()}`
          : `${diff} behind the leader`;
      }
    } else {
      this.kpiRankTotal = 1;
      this.kpiRank = 1;
      this.kpiRankNote = 'No competitor tracked';
    }
  }

  private buildCatCards() {
    const scores = this.overviewData?.scores || {};
    const sections = this.sectionsData || {};
    const tSeries = this.targetSeries();

    // Always show the full known set (UX, CR, SEO, GEO, Technical, Diagnostics);
    // categories the API doesn't return yet render as "No data".
    const keys = [
      ...CAT_ORDER,
      ...Object.keys(scores).filter((k) => k !== 'overall' && !CAT_ORDER.includes(k)),
    ];

    this.catCards = keys.map((key) => {
      let s = scores[key] || {};
      // SEO/GEO aren't in the audit payload — merge in the keywords-summary
      // scores (target + selected competitor) fetched separately.
      if ((s.target === null || s.target === undefined) && this.seoGeoScores[key]) {
        s = { ...s, ...this.seoGeoScores[key] };
      }
      const sec = sections[key] || {};
      // Diagnostics: higher = more errors = worse, so good/bad reads inverted.
      const inverted = this.isInverted(key);
      const hasScore = s.target !== null && s.target !== undefined;
      const score = hasScore ? Math.round(s.target) : null;
      const delta = this.roundDelta(s.targetDelta);
      // A rising score is "good" normally, but "bad" for an inverted metric
      // (more errors). The arrow still shows the numeric direction; only the
      // color conveys good/bad.
      const deltaGood = delta === null ? null : inverted ? delta < 0 : delta > 0;
      const deltaColor =
        deltaGood === null ? this.gray : deltaGood ? this.green : this.red;
      const comp = s.competitor;
      const hasComp = comp !== null && comp !== undefined;
      const diff = hasScore && hasComp ? (score as number) - Math.round(comp) : null;
      // "Ahead" = the better side — the higher score normally, the lower one
      // (fewer errors) for an inverted metric.
      const ahead = diff === null ? null : inverted ? diff <= 0 : diff >= 0;
      const statusColor = hasScore
        ? this.impactColor(score as number, inverted)
        : this.gray;

      const spark = (tSeries?.scores?.[key] || []).filter(
        (v: any) => v !== null && v !== undefined,
      );
      // A flat series (every point identical) conveys no trend — hide the
      // sparkline entirely for those cards (#12).
      const sparkFlat = spark.length > 1 && new Set(spark).size === 1;
      // Color the sparkline by its OWN drawn direction (up = good → green for a
      // normal category), so it reflects the trend even when the API sends no
      // delta. Falls back to the delta color when there's no usable series.
      let sparkColor = deltaColor;
      if (spark.length > 1 && !sparkFlat) {
        const trend = spark[spark.length - 1] - spark[0];
        const trendGood = inverted ? trend < 0 : trend > 0;
        sparkColor = trend === 0 ? this.gray : trendGood ? this.green : this.red;
      }

      return {
        key,
        label: sec?.label || CAT_LABELS[key] || key.toUpperCase(),
        tabIndex: CAT_TAB_INDEX[key] ?? null,
        failed: !hasScore,
        score,
        status: hasScore ? this.impactLabel(score as number, inverted) : 'No data',
        statusColor,
        statusBg: this.rgba(statusColor, 0.13),
        delta,
        deltaColor,
        deltaArrow: delta === null ? '' : delta > 0 ? '▲' : delta < 0 ? '▼' : '→',
        deltaText: delta === null ? '' : `${delta > 0 ? '+' : ''}${delta} vs last`,
        issues: sec?.issueCounts?.targetCurrent ?? 0,
        spark,
        sparkFlat,
        sparkColor,
        hasComp,
        // A competitor is tracked but this category has no competitor score —
        // i.e. the competitor couldn't be crawled for it.
        compMissing: hasScore && !!this.activeCompetitor && !hasComp,
        vsColor: ahead === null ? this.gray : ahead ? this.green : this.red,
        vsArrow: ahead === null ? '' : ahead ? '▲' : '▼',
        vsText:
          diff === null
            ? ''
            : `${Math.abs(diff)}${ahead ? ' ahead of ' : ' behind '}${this.getActiveCompetitorName()}`,
      };
    });
  }

  // Every tracked competitor from the history series, with a stable key, display
  // label and color (assigned by position so a competitor keeps its color even
  // when others are toggled off).
  private enrichedCompetitors(): {
    series: any;
    key: string;
    label: string;
    color: string;
  }[] {
    const comps = (this.overviewData?.scoreHistory?.series || []).filter(
      (s: any) => s?.type === 'competitor',
    );
    return comps.map((s: any, idx: number) => ({
      series: s,
      key:
        s?.competitorId ||
        this.getDomain(s?.domain || s?.url || s?.name || '') ||
        `comp-${idx}`,
      label:
        this.getDomain(s?.domain || s?.url || s?.name || '') ||
        `Competitor ${idx + 1}`,
      color: COMP_PALETTE[idx % COMP_PALETTE.length],
    }));
  }

  // Latest non-null overall score in a series (used for standings + ranking).
  private seriesLatestOverall(series: any): number | null {
    const arr = series?.scores?.overall || [];
    for (let i = arr.length - 1; i >= 0; i--) {
      const v = arr[i];
      if (typeof v === 'number' && !isNaN(v)) return v;
    }
    return null;
  }

  private isTrendSeriesVisible(key: string): boolean {
    return this.trendVisible[key] !== false;
  }

  // ── Trend legend interactions (#9/#15) — toggle individual competitor lines ──
  toggleTrendSeries(key: string) {
    if (key === 'target') return; // "Your site" is always shown
    this.trendVisible[key] = !this.isTrendSeriesVisible(key);
    this.buildOverallTrend();
  }

  private buildOverallTrend() {
    const dates: string[] = this.overviewData?.scoreHistory?.dates || [];
    const tSeries = this.targetSeries();
    const comps = this.enrichedCompetitors();

    // Build the legend model (target always shown; competitors default visible).
    this.trendSeries = [
      { key: 'target', label: 'Your site', color: ACCENT, isYou: true, visible: true },
      ...comps.map((c) => ({
        key: c.key,
        label: c.label,
        color: c.color,
        isYou: false,
        visible: this.isTrendSeriesVisible(c.key),
      })),
    ];

    const visibleComps = comps.filter((c) => this.isTrendSeriesVisible(c.key));

    // Each series is followed by an HTML tooltip column carrying the SAME
    // combined tooltip (date + all visible scores). Only the last is shown via
    // CSS, so leading nulls never leave a blank tooltip.
    const header: any[] = [
      'Period',
      'Your site',
      { role: 'tooltip', p: { html: true } },
    ];
    visibleComps.forEach((c) => {
      header.push(c.label, { role: 'tooltip', p: { html: true } });
    });

    // Numeric/continuous x-axis (index 0..n-1) so the first point sits at the
    // y-axis and the last touches the right edge (a discrete axis pads both).
    const rows = dates.map((d, i) => {
      const full = this.fmtDate(d);
      const you = this.numOrNull(tSeries?.scores?.overall?.[i]);
      const compVals = visibleComps.map((c) =>
        this.numOrNull(c.series?.scores?.overall?.[i]),
      );

      let tip =
        `<p class='tooltip-text'><b>${full}</b></p>` +
        `<p class='tooltip-text'><b>Your site: </b>${you !== null ? you : '—'}/100</p>`;
      visibleComps.forEach((c, idx) => {
        const cv = compVals[idx];
        if (cv !== null) {
          tip += `<p class='tooltip-text'><b>${c.label}: </b>${cv}/100</p>`;
        }
      });

      const row: any[] = [i, you, tip];
      visibleComps.forEach((c, idx) => {
        const cv = compVals[idx];
        row.push(cv, cv !== null ? tip : null);
      });
      return row;
    });

    const n = dates.length;
    this.overallLineChart.dataTable = rows.length ? [header, ...rows] : [];
    this.overallLineChart.options['colors'] = [
      ACCENT,
      ...visibleComps.map((c) => c.color),
    ];
    // We render our own interactive legend (chips), so hide the built-in one.
    this.overallLineChart.options.legend.position = 'none';
    this.overallLineChart.options.hAxis.ticks = dates.map((d, i) => ({
      v: i,
      f: this.fmtShort(d),
    }));
    this.overallLineChart.options.hAxis.viewWindow = { min: 0, max: n > 1 ? n - 1 : 1 };
    this.applyChartTheme();
    // Only a biweekly audit with more than one data point has a meaningful
    // trend — hide the chart for one-time audits and single-point histories.
    this.overallHasData = rows.length > 1 && this.isBiweekly;
    this.redrawOverallChart();
  }

  private numOrNull(v: any): number | null {
    // Scores over time are shown as whole numbers (e.g. 55.32 → 55).
    return typeof v === 'number' && !isNaN(v) ? Math.round(v) : null;
  }

  private applyChartTheme() {
    const o: any = this.overallLineChart.options;
    o.backgroundColor = 'transparent';
    if (this.isDark) {
      o.legend.textStyle.color = '#cfccc6';
      o.vAxis.gridlineColor = '#3b3a3a';
      o.vAxis.baselineColor = '#3b3a3a';
      o.vAxis.textStyle.color = '#cfccc6';
      o.hAxis.textStyle.color = '#cfccc6';
      o.crosshair.color = '#3b3a3a';
    } else {
      o.legend.textStyle.color = '#666';
      o.vAxis.gridlineColor = '#ededed';
      o.vAxis.baselineColor = '#c0c0c0';
      o.vAxis.textStyle.color = '#666';
      o.hAxis.textStyle.color = '#666';
      o.crosshair.color = '#eeeeee';
    }
  }

  private redrawOverallChart() {
    // ng2-google-charts: redraw in place when the object is mutated
    setTimeout(() => this.overallLineChart.component?.draw(this.overallLineChart), 0);
  }

  private buildTopIssues() {
    const sections = this.sectionsData || {};
    const all: any[] = [];

    Object.entries(sections).forEach(([sectionKey, sec]: [string, any]) => {
      const label = sec?.label || '';
      (sec?.topIssues || []).forEach((issue: any) => {
        // Sections don't all key the title/description the same way — read the
        // first field that has content so e.g. Technical issues aren't blank.
        const title =
          issue?.text ||
          issue?.title ||
          issue?.issue ||
          issue?.name ||
          issue?.label ||
          '';
        const desc =
          issue?.reason ||
          issue?.description ||
          issue?.explanation ||
          issue?.detail ||
          '';
        all.push({
          title,
          desc,
          // Pill text is the sub-category; color comes from the parent section.
          category: issue?.category || label,
          sectionKey,
          score: issue?.score,
        });
      });
    });

    this.topCriticalIssues = all
      .filter(
        (i) =>
          typeof i.score === 'number' && !!i.title && String(i.title).trim().length > 0,
      )
      .sort((a, b) => a.score - b.score)
      .slice(0, 5)
      .map((i, idx) => {
        const critical = i.score < 50;
        // Color by position so consecutive pills alternate (blue, purple, ...).
        const catColor = PILL_PALETTE[idx % PILL_PALETTE.length];
        return {
          ...i,
          sevColor: critical ? this.red : this.amber,
          sevBg: this.rgba(critical ? this.red : this.amber, 0.14),
          // Outlined pill: transparent bg, full-strength category color for
          // both text and 1px border (each category its own static color).
          catColor,
          catBorder: catColor,
        };
      });
  }

  private buildStandings() {
    const overall = this.overviewData?.scores?.overall;
    if (!overall || overall.target === null || overall.target === undefined) {
      this.standings = [];
      this.standingsNote = '';
      return;
    }

    const rows: any[] = [
      {
        name: 'Your site',
        score: Math.round(overall.target),
        isYou: true,
        color: ACCENT,
      },
    ];

    // Show every tracked competitor (no truncation, #13) using each one's latest
    // overall score from the history series. Fall back to the single selected
    // competitor for one-time audits that carry no per-competitor series.
    const enriched = this.enrichedCompetitors();
    if (enriched.length) {
      enriched.forEach((c) => {
        const score = this.seriesLatestOverall(c.series);
        if (score !== null) {
          rows.push({
            name: c.label,
            score: Math.round(score),
            isYou: false,
            color: c.color,
          });
        }
      });
    } else if (overall.competitor !== null && overall.competitor !== undefined) {
      rows.push({
        name: this.getActiveCompetitorName() || 'Competitor',
        score: Math.round(overall.competitor),
        isYou: false,
        color: COMP_COLOR,
      });
    }

    const hasComp = rows.length > 1;
    rows.sort((a, b) => b.score - a.score);

    this.standings = rows.map((r, i) => ({
      ...r,
      rank: i + 1,
      barWidth: `${r.score}%`,
      barColor: r.color,
      nameColor: r.isYou ? 'var(--wa-lastval)' : 'var(--wa-t1)',
      weight: 500,
    }));

    // The note frames strongest/weakest areas as a benchmark ("vs {competitor}"),
    // so it only makes sense while a competitor is tracked. With none, the
    // standings card is just your own single row — no comparison note.
    const sum = this.overviewData?.summary;
    const compName = this.getActiveCompetitorName();
    if (sum && hasComp && compName) {
      const parts: string[] = [];
      if (sum.strongestArea) parts.push(`strongest in ${sum.strongestArea}`);
      if (sum.weakestArea) parts.push(`weakest in ${sum.weakestArea}`);
      this.standingsNote = parts.length
        ? `You're ${parts.join(', ')} vs ${compName}.`
        : '';
    } else {
      this.standingsNote = '';
    }
  }

  // ─── Loader ────────────────────────────────────────────────────────────────

  private updateLoaderState() {
    const status = (this.selectedTask?.status || '').toString().toLowerCase();
    const done = status === 'done' || status === 'finished';
    this.showLoader = !this.overviewData && !done && !!this.selectedTask;
    this.loaderPercentage = this.showLoader ? 40 : 100;
    this.loaderLineIndex = Math.max(
      0,
      Math.round(this.loaderPercentage / this.loaderTexts.length) - 1,
    );
  }

  // ─── Navigation ──────────────────────────────────────────────────────────────

  goToTabIndex(index: number | null) {
    if (index === null || index === undefined) return;
    this.sidenavService.updateActiveTabIndex(index);
  }

  addAudit() {
    sessionStorage.setItem('addAudit', 'true');
    this.sidenavService.updateActiveTabIndex(8);
  }

  // Show an "Add competitor" CTA in Overall Standings when nothing is being
  // benchmarked yet (mirrors the sub-bar's add button; hidden for crawler tasks
  // and in the shared read-only view).
  get canAddCompetitor(): boolean {
    return !this.isShare && this.selectedTask?.type !== 'Crawler';
  }

  addCompetitor() {
    const parentTaskId = this.resolveParentTaskId();
    if (!parentTaskId) return;

    const competitors = this.selectedTask?.availableCompetitors || this.competitors || [];
    const ref = this.dialog.open(AddCompetitorComponent, {
      width: '430px',
      data: {
        taskId: parentTaskId,
        competitors,
        // Source list already is the benchmark set → all shown active.
        activeCompetitorIds: competitors
          .map((c: any) => c?.competitorId)
          .filter(Boolean),
      },
    });

    this.subs.add(
      ref.afterClosed().subscribe((result: any) => {
        if (!result) return;
        const taskId = this.selectedTask?.taskId;
        if (!taskId) return;
        // Refetch the task and broadcast it so the standings (and every other
        // tab) re-hydrate with the newly added competitor.
        this.websiteAuditService.getAuditTask(taskId).subscribe({
          next: (res: any) => {
            this.websiteAuditService.updateSelectedAuditTask({
              ...this.selectedTask,
              ...res,
            });
          },
        });
      }),
    );
  }

  // Competitors live on the biweekly parent task, not the individual run.
  private resolveParentTaskId(): string | null {
    const t = this.selectedTask;
    if (!t) return null;
    if (t.parentTaskId) return t.parentTaskId;
    const hist = Array.isArray(t.taskHistory) ? t.taskHistory : [];
    const root = hist.find((h: any) => h?.taskId && h.taskId === h?.parentTaskId);
    return root?.taskId || t.taskId || null;
  }

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  // One-time audits have no history, so trend charts are hidden for them.
  get isBiweekly(): boolean {
    return (this.selectedTask?.auditFrequency || '') === 'biweekly';
  }

  getActiveCompetitorName(): string {
    return this.getDomain(
      this.activeCompetitor?.domain || this.activeCompetitor?.url || '',
    );
  }

  getCompChipLabel(comp: any): string {
    return comp?.domain || this.getDomain(comp?.url || '');
  }

  private sumSectionIssues(): number {
    const sections = this.sectionsData || {};
    return Object.values(sections).reduce(
      (sum: number, sec: any) => sum + (sec?.issueCounts?.targetCurrent || 0),
      0,
    );
  }

  private catKeyFromLabel(label: string): string {
    const l = (label || '').toLowerCase();
    for (const k of Object.keys(CAT_LABELS)) {
      if (CAT_LABELS[k].toLowerCase() === l || k === l) return k;
    }
    return '';
  }

  // Whether a category's score scale is inverted (higher = worse), e.g.
  // Diagnostics, which counts errors/issues.
  private isInverted(key: string): boolean {
    return INVERTED_SCORE_KEYS.includes(key);
  }

  impactLabel(num: number, inverted = false): string {
    if (typeof num !== 'number') return 'No data';
    if (inverted) {
      if (num >= 80) return 'Critical';
      if (num >= 50) return 'Warning';
      return 'Good';
    }
    if (num < 50) return 'Critical';
    if (num < 80) return 'Warning';
    return 'Good';
  }
  impactColor(num: number, inverted = false): string {
    if (typeof num !== 'number') return this.gray;
    if (inverted) {
      if (num >= 80) return this.red;
      if (num >= 50) return this.amber;
      return this.green;
    }
    if (num < 50) return this.red;
    if (num < 80) return this.amber;
    return this.green;
  }
  impactClass(num: number): string {
    if (typeof num !== 'number') return '';
    if (num < 50) return 'critical';
    if (num < 80) return 'warning';
    return 'good';
  }

  private roundDelta(d: any): number | null {
    if (d === null || d === undefined) return null;
    return Math.round(d * 10) / 10;
  }

  rgba(hex: string, a: number): string {
    const h = (hex || '').replace('#', '');
    if (h.length < 6) return `rgba(107,102,96,${a})`;
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }

  getDomain(url: string): string {
    if (!url) return '';
    try {
      const normalized = url.startsWith('http') ? url : `https://${url}`;
      return new URL(normalized).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  }

  getAuditFrequencyLabel(): string {
    const freq = this.selectedTask?.auditFrequency;
    if (!freq) return 'ONE-TIME';
    return freq === 'biweekly' ? 'BIWEEKLY' : freq.toUpperCase();
  }

  getAuditDateLabel(): string {
    const date = this.selectedTask?.updatedAt || this.selectedTask?.createdAt;
    return date ? this.fmtDate(date) : '';
  }

  private fmtDate(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return '';
    }
  }
  private fmtShort(date: string): string {
    try {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return date;
    }
  }
}
